'use client';

import { Button } from '@components/common';
import { CheckIcon } from '@heroicons/react/16/solid';
import { useTranslation } from 'next-i18next';
import { dataTestId } from '@services/data-test-id';
import { showBuyNeuroModal } from '@components/features/modals/buy-neuro/BuyNeuroModal';
import { useExtendedRouter } from '@hooks/next-routing-wrappers';

interface DiscountBox {
  percentage: number;
  titleKey: string;
  benefits: string[];
  minAmount: number;
}

interface DiscountBoxesProps {
  currentDiscountPercentage?: number;
  isAuth: boolean;
  showOnlyNext?: boolean;
}

/**
 * @description Displays the discount tiers (15%, 20%, 25%)
 */
export const PremiumDiscountBoxes = ({
  currentDiscountPercentage,
  isAuth,
  showOnlyNext,
}: DiscountBoxesProps) => {
  const { t } = useTranslation('common');
  const { router } = useExtendedRouter();

  const discountBoxes: DiscountBox[] = [
    {
      percentage: 15,
      titleKey: 'consultant.info.box.1.title',
      benefits: [
        t('premium.15.benefit.5.name'),
        t('premium.15.benefit.4.name'),
        t('premium.15.benefit.1.name'),
        t('premium.15.benefit.2.name'),
        t('premium.15.benefit.3.name'),
        t('premium.15.benefit.6.name'),
        t('premium.15.benefit.7.name'),
      ],
      minAmount: 2000,
    },
    {
      percentage: 20,
      titleKey: 'consultant.info.box.2.title',
      benefits: [
        t('premium.20.benefit.5.name'),
        t('premium.20.benefit.4.name'),
        t('premium.20.benefit.1.name'),
        t('premium.20.benefit.2.name'),
        t('premium.20.benefit.3.name'),
        t('premium.20.benefit.6.name'),
        t('premium.20.benefit.7.name'),
      ],
      minAmount: 5000,
    },
    {
      percentage: 25,
      titleKey: 'consultant.info.box.3.title',
      benefits: [
        t('premium.25.benefit.5.name'),
        t('premium.25.benefit.4.name'),
        t('premium.25.benefit.1.name'),
        t('premium.25.benefit.2.name'),
        t('premium.25.benefit.3.name'),
        t('premium.25.benefit.6.name'),
        t('premium.25.benefit.7.name'),
      ],
      minAmount: 10000,
    },
  ];

  let boxesToShow = discountBoxes;
  if (showOnlyNext) {
    const currentIndex = discountBoxes.findIndex(
      (b) => currentDiscountPercentage === b.percentage
        || (b.percentage === 25 && (currentDiscountPercentage || 0) > 25),
    );

    if (currentIndex === -1) {
      // No active → show the first
      boxesToShow = [discountBoxes[0]];
    } else if (currentIndex < discountBoxes.length - 1) {
      // Active but not last → show next
      boxesToShow = [discountBoxes[currentIndex + 1]];
    } else {
      // Last active → show itself only
      boxesToShow = [discountBoxes[currentIndex]];
    }
  }

  return (
    <div className={`${showOnlyNext ? 'grid gap-5 md:grid-cols-1' : 'grid gap-5 md:grid-cols-3'}`}>
      {boxesToShow.map((box) => {
        const isActive = currentDiscountPercentage !== undefined
          && (currentDiscountPercentage === box.percentage
            || (box.percentage === 25 && currentDiscountPercentage > 25));

        const isRecommended = box.percentage === 20
          && !isActive
          && (currentDiscountPercentage || 0) < box.percentage;

        return (
          <div
            key={box.percentage}
            className={`${
              isActive || isRecommended
                ? 'rounded-md border border-dashed border-secondary'
                : 'rounded-md border'
            } relative flex h-full flex-col p-4 text-center`}
          >
            {isActive && (
              <div className="absolute -top-2 left-2 flex rounded-full bg-secondary px-2 py-0.5 text-pico text-white">
                {t('global.label.active.benefit')}
              </div>
            )}
            {isRecommended && (
              <div className="absolute -top-2 left-2 flex rounded-full bg-secondary px-2 py-0.5 text-pico text-white">
                {t('global.label.recommended')}
              </div>
            )}

            <div className="text-left font-bold md:min-h-[50px]">
              {t(box.titleKey)}
            </div>

            <div className="flex flex-col gap-2 py-1 text-left text-sm leading-tight">
              {box.benefits.map((benefit) => (
                <div
                  key={benefit}
                  className="flex gap-2 pb-1 text-sm leading-tight"
                >
                  <CheckIcon className="size-5 text-secondary" />
                  <span>{benefit}</span>
                </div>
              ))}
            </div>

            {(currentDiscountPercentage || 0) < box.percentage && (
              <Button
                styling="none"
                rounding="full"
                size="sm"
                className="mt-5 block w-fit rounded-full bg-secondary px-6 py-3 text-sm font-medium text-surface"
                onClick={() => {
                  if (!isAuth) {
                    void router.push('/login', { withLocale: false });
                    return;
                  }
                  showBuyNeuroModal(box.minAmount);
                }}
                {...dataTestId('companyOfferPage.activatePremiumButton0')}
              >
                {t('subscription.package.activate')}
              </Button>
            )}
          </div>
        );
      })}
    </div>
  );
};
